import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// モジュール変数で保持。戻るナビで再マウント時も beforeinstallprompt は再発火しないため、保持が必要
let storedPrompt: BeforeInstallPromptEvent | null = null;

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() => storedPrompt);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // 既に PWA として開いているか（display-mode: standalone または fullscreen）
    const checkInstalled = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches;
    setIsInstalled(checkInstalled());
    const mqStandalone = window.matchMedia('(display-mode: standalone)');
    const mqFullscreen = window.matchMedia('(display-mode: fullscreen)');
    const handler = () => setIsInstalled(checkInstalled());
    mqStandalone.addEventListener('change', handler);
    mqFullscreen.addEventListener('change', handler);

    // 保持済みがあれば同期（戻るナビ後の再マウント用）
    if (storedPrompt) setDeferredPrompt(storedPrompt);

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const ev = e as BeforeInstallPromptEvent;
      storedPrompt = ev;
      setDeferredPrompt(ev);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    return () => {
      mqStandalone.removeEventListener('change', handler);
      mqFullscreen.removeEventListener('change', handler);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    const prompt = deferredPrompt ?? storedPrompt;
    if (!prompt) return false;
    try {
      const { outcome } = await prompt.prompt();
      if (outcome === 'accepted') {
        storedPrompt = null;
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  return {
    isInstalled,
    canInstall: !!(deferredPrompt ?? storedPrompt),
    promptInstall,
  };
}
